import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import { BulkRemindPanel } from "./BulkRemindPanel";
import {
  CalendarClock, Upload, Trash2, Loader2, RefreshCw,
  CheckCircle2, Clock, Download, FileText, AlertTriangle, X,
} from "lucide-react";
import NotifyDocumentWhatsAppDialog from "./NotifyDocumentWhatsAppDialog";

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR"); }
  catch { return iso; }
};

// ===== Upload Dialog =====
function UploadAdvancesDialog({ open, onOpenChange, onUploaded }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  // After a dry-run upload we land in a "preview" state and the user
  // must explicitly confirm or cancel.
  const [preview, setPreview] = useState(null); // { staging_id, matched, not_found, ... }
  const [unchecked, setUnchecked] = useState(new Set()); // employee_ids to skip
  const [result, setResult] = useState(null);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyRecipients, setNotifyRecipients] = useState([]);
  // PDF preview viewer state (mirrors AdminVacationNoticesTab)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [progress, setProgress] = useState({ phase: "idle", uploadPct: 0, processed: 0, total: 0 });
  const inputRef = useRef(null);
  const pollTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setFiles([]); setResult(null); setUploading(false);
      setPreview(null); setUnchecked(new Set());
      setSelectedEmployeeId(null);
      if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-select first matched employee when preview arrives.
  useEffect(() => {
    if (preview && (preview.matched || []).length > 0 && !selectedEmployeeId) {
      setSelectedEmployeeId(preview.matched[0].employee_id);
    }
  }, [preview, selectedEmployeeId]);

  // Load the modern PDF for the currently selected employee.
  useEffect(() => {
    const stagingId = preview?.staging_id;
    if (!stagingId || !selectedEmployeeId) return;
    let cancelled = false;
    setLoadingPdf(true);
    axios
      .get(
        `${API}/admin/vacation-advances/staging/${stagingId}/pdf/${selectedEmployeeId}`,
        { responseType: "blob", timeout: 30000 },
      )
      .then((res) => {
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        setPreviewPdfUrl((old) => {
          if (old) URL.revokeObjectURL(old);
          return url;
        });
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Erro ao carregar a pré-visualização do PDF");
      })
      .finally(() => {
        if (!cancelled) setLoadingPdf(false);
      });
    return () => { cancelled = true; };
  }, [preview?.staging_id, selectedEmployeeId]);

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setFiles((prev) => [...prev, ...picked]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeFile = (i) =>
    setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!files.length) {
      toast.error("Selecione ao menos um PDF");
      return;
    }
    setUploading(true);
    setResult(null);
    setPreview(null);
    setUnchecked(new Set());
    setProgress({ phase: "uploading", uploadPct: 0, processed: 0, total: 0 });
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      // Async job: returns job_id immediately, then we poll.
      const startRes = await axios.post(
        `${API}/admin/vacation-advances/upload-async?dry_run=true`, fd,
        {
          timeout: 120000,
          onUploadProgress: (ev) => {
            if (!ev.total) return;
            const pct = Math.round((ev.loaded * 100) / ev.total);
            setProgress((p) => ({ ...p, phase: "uploading", uploadPct: pct }));
          },
        },
      );
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("Resposta inválida do servidor");
      setProgress((p) => ({ ...p, phase: "processing", uploadPct: 100 }));

      // Poll every 800ms until done or error.
      await new Promise((resolve, reject) => {
        pollTimerRef.current = setInterval(async () => {
          try {
            const r = await axios.get(
              `${API}/admin/vacation-advances/upload-job/${jobId}`,
              { timeout: 15000 },
            );
            const job = r.data || {};
            setProgress((p) => ({
              ...p,
              phase: "processing",
              processed: job.processed || 0,
              total: job.total || 0,
            }));
            if (job.status === "done") {
              clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
              const data = job.result || {};
              setPreview(data);
              if ((data.count || 0) === 0) {
                toast.error("Nenhum funcionário identificado no PDF.");
              }
              resolve();
            } else if (job.status === "error") {
              clearInterval(pollTimerRef.current);
              pollTimerRef.current = null;
              reject(new Error(job.error || "Erro ao processar PDFs"));
            }
          } catch (err) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
            reject(err);
          }
        }, 800);
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Falha no upload");
    } finally {
      setUploading(false);
      setProgress({ phase: "idle", uploadPct: 0, processed: 0, total: 0 });
    }
  };

  const toggleEmployee = (employeeId) => {
    setUnchecked((prev) => {
      const next = new Set(prev);
      if (next.has(employeeId)) next.delete(employeeId);
      else next.add(employeeId);
      return next;
    });
  };

  const confirmStaging = async () => {
    if (!preview?.staging_id) return;
    const keepIds = (preview.matched || [])
      .map((m) => m.employee_id)
      .filter((id) => !unchecked.has(id));
    if (!keepIds.length) {
      toast.error("Marque pelo menos um funcionário para enviar.");
      return;
    }
    setCommitting(true);
    try {
      const res = await axios.post(
        `${API}/admin/vacation-advances/commit-staging/${preview.staging_id}`,
        { keep_employee_ids: keepIds },
        { timeout: 60000 },
      );
      const okN = res.data?.count || 0;
      if (okN) {
        toast.success(`${okN} aviso(s) criado(s)`);
        setResult(res.data);
        setNotifyRecipients(res.data.matched || []);
        setNotifyOpen(true);
        setPreview(null);
        setFiles([]);
        setSelectedEmployeeId(null);
        if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
        setPreviewPdfUrl(null);
      }
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao confirmar");
    } finally {
      setCommitting(false);
    }
  };

  const cancelPreview = async () => {
    if (preview?.staging_id) {
      try {
        await axios.delete(
          `${API}/admin/vacation-advances/commit-staging/${preview.staging_id}`,
        );
      } catch { /* best-effort */ }
    }
    setPreview(null);
    setUnchecked(new Set());
    setSelectedEmployeeId(null);
    if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
    setPreviewPdfUrl(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={preview ? "max-w-6xl" : "max-w-2xl"}
        data-testid="va-upload-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-amber-600" />
            Upload de Avisos de Férias
          </DialogTitle>
          <DialogDescription>
            Envie PDFs com <strong>1 funcionário por página</strong>. O sistema
            identifica cada um pela <strong>CHAPA (matrícula)</strong> e nome.
            Os funcionários assinam digitalmente via SMS.
          </DialogDescription>
        </DialogHeader>

        <div
          className="border-2 border-dashed border-amber-200 bg-amber-50/40 rounded-lg p-6 text-center"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const dropped = Array.from(e.dataTransfer.files || []).filter(
              (f) => f.type === "application/pdf"
                || f.name.toLowerCase().endsWith(".pdf"),
            );
            setFiles((prev) => [...prev, ...dropped]);
          }}
        >
          <Upload className="w-8 h-8 mx-auto text-amber-600 mb-2" />
          <p className="text-sm text-slate-700">
            Arraste PDFs aqui ou{" "}
            <button
              type="button"
              className="text-amber-700 underline"
              onClick={() => inputRef.current?.click()}
              data-testid="va-pick-files-btn"
            >
              selecione do computador
            </button>
          </p>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf"
            multiple
            className="hidden"
            onChange={onPick}
          />
        </div>

        {files.length > 0 && (
          <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
            {files.map((f, i) => (
              <div
                key={i}
                className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1.5"
              >
                <span className="truncate flex-1 mr-2">{f.name}</span>
                <span className="text-slate-500 mr-2">
                  {(f.size / 1024).toFixed(0)} KB
                </span>
                <button
                  className="text-rose-600 hover:text-rose-800"
                  onClick={() => removeFile(i)}
                  data-testid={`va-remove-file-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* PREVIEW state — admin must confirm before commit */}
        {preview && (
          <div className="mt-3 space-y-2" data-testid="va-preview-block">
            <div className="flex items-center justify-between gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-md">
              <p className="text-xs text-emerald-800 font-medium">
                <CheckCircle2 className="inline w-3.5 h-3.5 mr-1" />
                {(preview.matched || []).length} funcionário(s) identificado(s)
              </p>
              <span className="text-[11px] text-emerald-700">
                {(preview.matched || []).length - unchecked.size} marcado(s) p/ envio
              </span>
            </div>

            {(preview.matched || []).length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-3">
                {/* LEFT — employee list */}
                <div className="border border-slate-200 rounded-md max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
                  {preview.matched.map((m, i) => {
                    const skipped = unchecked.has(m.employee_id);
                    const selected = selectedEmployeeId === m.employee_id;
                    return (
                      <div
                        key={i}
                        className={`flex items-center gap-2 px-3 py-1.5 text-xs transition cursor-pointer ${
                          selected
                            ? "bg-amber-50 border-l-2 border-l-amber-500"
                            : "hover:bg-slate-50 border-l-2 border-l-transparent"
                        } ${skipped ? "opacity-60" : ""}`}
                        data-testid={`va-preview-row-${m.matricula}`}
                        onClick={() => setSelectedEmployeeId(m.employee_id)}
                      >
                        <input
                          type="checkbox"
                          checked={!skipped}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleEmployee(m.employee_id)}
                          className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                          data-testid={`va-preview-checkbox-${m.matricula}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">
                            {m.nome}
                          </p>
                          <p className="text-[10px] text-slate-500 font-mono">
                            Mat. {m.matricula} · {m.filename}
                          </p>
                        </div>
                        {selected && (
                          <FileText className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* RIGHT — PDF preview iframe */}
                <div
                  className="border border-slate-200 rounded-md bg-slate-50 flex flex-col min-h-[60vh] max-h-[60vh]"
                  data-testid="va-preview-pdf-viewer"
                >
                  <div className="px-3 py-1.5 border-b border-slate-200 flex items-center justify-between gap-2 bg-white text-[11px]">
                    <span className="font-semibold text-slate-700 truncate flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5 text-amber-600" />
                      {selectedEmployeeId
                        ? (preview.matched.find(
                            (m) => m.employee_id === selectedEmployeeId,
                          )?.nome || "Pré-visualização")
                        : "Selecione um funcionário"}
                    </span>
                    {loadingPdf && (
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 overflow-hidden">
                    {previewPdfUrl && !loadingPdf ? (
                      <iframe
                        src={previewPdfUrl}
                        title="Pré-visualização Aviso de Férias"
                        className="w-full h-full"
                        data-testid="va-preview-pdf-iframe"
                      />
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-400 text-xs gap-2">
                        {loadingPdf ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Carregando PDF…
                          </>
                        ) : (
                          <>Clique em um funcionário à esquerda</>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {(preview.not_found || []).length > 0 && (
              <details
                className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs"
                data-testid="va-preview-notfound"
              >
                <summary className="cursor-pointer">
                  <AlertTriangle className="inline w-3.5 h-3.5 mr-1 text-amber-700" />
                  <strong>{preview.not_found.length}</strong> página(s) não associada(s) — não serão enviadas
                </summary>
                <ul className="mt-1 space-y-0.5 ml-4 list-disc">
                  {preview.not_found.slice(0, 30).map((n, i) => (
                    <li key={i}>
                      <span className="font-mono">{n.filename}</span> — {n.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-2 mt-2">
            {result.count > 0 && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-xs">
                <CheckCircle2 className="inline w-3 h-3 mr-1 text-emerald-700" />
                <strong>{result.count}</strong> aviso(s) criado(s) com sucesso.
              </div>
            )}
            {result.not_found?.length > 0 && (
              <details className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs">
                <summary className="cursor-pointer">
                  <AlertTriangle className="inline w-3 h-3 mr-1 text-amber-700" />
                  <strong>{result.not_found.length}</strong> página(s) não associada(s)
                </summary>
                <ul className="mt-1 space-y-0.5 ml-4 list-disc">
                  {result.not_found.slice(0, 30).map((n, i) => (
                    <li key={i}>
                      <span className="font-mono">{n.filename}</span> — {n.reason}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <DialogFooter>
          {preview ? (
            <>
              <Button
                variant="outline"
                onClick={cancelPreview}
                disabled={committing}
                data-testid="va-preview-cancel-btn"
              >
                <X className="w-3 h-3 mr-1" />
                Cancelar (voltar)
              </Button>
              <Button
                onClick={confirmStaging}
                disabled={committing || (preview.matched || []).length === 0 || unchecked.size === (preview.matched || []).length}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="va-preview-confirm-btn"
              >
                {committing
                  ? (<><Loader2 className="w-3 h-3 mr-1 animate-spin" />Enviando…</>)
                  : (
                    <>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                      Confirmar envio ({(preview.matched || []).length - unchecked.size})
                    </>
                  )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={uploading}
              >
                Fechar
              </Button>
              <Button
                onClick={submit}
                disabled={uploading || !files.length}
                data-testid="va-upload-submit-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    {progress.phase === "uploading"
                      ? `Enviando ${progress.uploadPct}%`
                      : progress.total > 0
                        ? `Processando ${progress.processed}/${progress.total}`
                        : "Processando…"}
                  </>
                ) : (
                  <>Pré-visualizar {files.length || ""}</>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>

      <NotifyDocumentWhatsAppDialog
        open={notifyOpen}
        onOpenChange={setNotifyOpen}
        documentLabel="Aviso de Férias"
        documentType="aviso-ferias"
        recipients={notifyRecipients}
      />
    </Dialog>
  );
}


// ===== Detail of a batch =====
function BatchDetailDialog({ noticeId, open, onOpenChange, onReload }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [deletingRid, setDeletingRid] = useState(null);

  const load = useCallback(async () => {
    if (!noticeId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/vacation-advances/${noticeId}`);
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error("Erro ao carregar detalhes");
    } finally {
      setLoading(false);
    }
  }, [noticeId]);

  useEffect(() => {
    if (open && noticeId) load();
  }, [open, noticeId, load]);

  const downloadOne = async (it) => {
    try {
      const r = await axios.get(
        `${API}/admin/vacation-advances/${noticeId}/pdf/${it._recipient_id}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        `aviso_${it.employee_matricula || it._recipient_id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erro ao baixar PDF");
    }
  };

  const deleteRecipient = async (it) => {
    const nome = it.employee_nome || it.employee_matricula || "este funcionário";
    if (!window.confirm(
      `Excluir apenas o aviso de "${nome}"?\n\nO restante do lote fica intacto.\nEsta ação não pode ser desfeita.`,
    )) return;
    setDeletingRid(it._recipient_id);
    try {
      await axios.delete(
        `${API}/admin/vacation-advances/${noticeId}`
        + `/recipient/${encodeURIComponent(it._recipient_id)}`,
      );
      toast.success("Aviso excluído.");
      // Optimistically hide the row and refetch the outer batch list.
      setItems((prev) => prev.filter((x) => x._recipient_id !== it._recipient_id));
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir.");
    } finally {
      setDeletingRid(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Avisos do lote</DialogTitle>
          <DialogDescription>
            {items.length} funcionário(s) — clique em baixar para ver o PDF
            (original enquanto pendente, assinado quando finalizado).
          </DialogDescription>
        </DialogHeader>
        {/* Bulk cobrança WhatsApp — only for pending recipients */}
        <BulkRemindPanel
          previewUrl={`/admin/vacation-advances/${noticeId}/bulk-remind/preview`}
          fireUrl={`/admin/vacation-advances/${noticeId}/bulk-remind`}
          hasPending={items.some((it) => it.status !== "signed")}
          docLabel="Aviso Prévio de Férias pendente"
          onDone={load}
        />
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left p-2">Mat.</th>
                <th className="text-left p-2">Funcionário</th>
                <th className="text-left p-2">Contato</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Assinado em</th>
                <th className="text-right p-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const wa = (it.employee_whatsapp || "").trim();
                const tel = (it.employee_telefone || "").trim();
                // Only trust `wa` when the employee explicitly
                // confirmed the number (status="confirmed" via the
                // WhatsApp opt-in flow). Otherwise render just the
                // regular phone with no green icon.
                const isWa =
                  !!wa && it.employee_whatsapp_status === "confirmed";
                // Build a click-to-open link when WhatsApp is
                // confirmed — strip +/spaces to end up with a raw
                // E.164 number wa.me accepts.
                const waHref = isWa
                  ? `https://wa.me/${wa.replace(/[^0-9]/g, "")}`
                  : null;
                const shown = isWa ? wa : tel;
                return (
                <tr key={it._recipient_id} className="border-t border-slate-100">
                  <td className="p-2 font-mono text-blue-600">
                    {it.employee_matricula || "—"}
                  </td>
                  <td className="p-2">{it.employee_nome || it.extracted_nome}</td>
                  <td className="p-2">
                    {shown ? (
                      isWa ? (
                        <a
                          href={waHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-emerald-700 hover:text-emerald-900 hover:underline"
                          data-testid={`va-wa-${it._recipient_id}`}
                          title="Abrir conversa no WhatsApp"
                        >
                          <svg
                            className="w-3.5 h-3.5 shrink-0"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-label="WhatsApp"
                          >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                            <path d="M20.52 3.449C12.831-3.984.106 1.407.101 11.893a11.9 11.9 0 0 0 1.6 5.945L0 24l6.335-1.652c9.98 5.404 22.037-1.816 22.037-10.451 0-3.176-1.237-6.165-3.485-8.416l-.043-.032zM12.05 21.785h-.005a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.981.999-3.648-.235-.374a9.86 9.86 0 0 1-1.511-5.263c.003-5.45 4.436-9.884 9.892-9.884 2.641 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.436 9.884-9.888 9.884z"/>
                          </svg>
                          <span className="font-mono text-[11px]">
                            {shown}
                          </span>
                        </a>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1.5 text-slate-600"
                          data-testid={`va-phone-${it._recipient_id}`}
                          title="Telefone (não confirmado como WhatsApp)"
                        >
                          <svg
                            className="w-3.5 h-3.5 shrink-0 text-slate-400"
                            fill="none" stroke="currentColor"
                            strokeWidth="2" viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round"
                              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <span className="font-mono text-[11px]">
                            {shown}
                          </span>
                        </span>
                      )
                    ) : (
                      <span className="text-slate-400 italic">—</span>
                    )}
                  </td>
                  <td className="p-2">
                    {it.status === "signed" ? (
                      <Badge className="bg-emerald-600">Assinado</Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-700 border-amber-300">
                        Pendente
                      </Badge>
                    )}
                  </td>
                  <td className="p-2 text-slate-600">
                    {formatDateTime(it.signed_at)}
                  </td>
                  <td className="p-2 text-right">
                    <div className="flex items-center gap-1.5 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px]"
                        onClick={() => downloadOne(it)}
                        data-testid={`va-download-${it._recipient_id}`}
                      >
                        <Download className="w-3 h-3 mr-1" />
                        Baixar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 border-red-200 text-red-600 hover:bg-red-50"
                        onClick={() => deleteRecipient(it)}
                        disabled={deletingRid === it._recipient_id}
                        data-testid={`va-delete-${it._recipient_id}`}
                        title="Excluir este aviso (só do funcionário)"
                      >
                        {deletingRid === it._recipient_id
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Trash2 className="w-3 h-3" />}
                      </Button>
                    </div>
                  </td>
                </tr>
                );
              })}
              {!items.length && (
                <tr>
                  <td colSpan={6} className="text-center text-slate-500 p-6">
                    Nenhum funcionário neste lote.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ===== Main tab =====
export default function AdminVacationAdvancesTab() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/vacation-advances`);
      setBatches(r.data || []);
    } catch (e) {
      toast.error("Erro ao listar lotes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (id) => {
    if (!confirm("Excluir todo o lote? Todos os avisos pendentes serão removidos."))
      return;
    try {
      await axios.delete(`${API}/admin/vacation-advances/${id}`);
      toast.success("Lote excluído");
      load();
    } catch (e) {
      toast.error("Erro ao excluir");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-amber-600" />
            Avisos de Férias
          </h2>
          <p className="text-sm text-slate-500">
            Envie PDFs com vários funcionários (1 por página). Identificação
            por <strong>matrícula</strong> + nome. Assinatura via SMS.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="va-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" />
            Atualizar
          </Button>
          <Button
            onClick={() => setUploadOpen(true)}
            data-testid="va-upload-btn"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Upload className="w-4 h-4 mr-1" />
            Upload de PDFs
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : !batches.length ? (
          <div className="text-center py-12 text-slate-500">
            <CalendarClock className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p>Nenhum lote criado</p>
            <p className="text-xs">
              Clique em "Upload de PDFs" para criar o primeiro.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left p-3">Lote</th>
                <th className="text-left p-3">Criado</th>
                <th className="text-left p-3">Funcionários</th>
                <th className="text-left p-3">Progresso</th>
                <th className="text-right p-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <motion.tr
                  key={b.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="border-t border-slate-100 hover:bg-slate-50"
                  data-testid={`va-batch-${b.id}`}
                >
                  <td className="p-3 font-mono text-xs text-blue-600">
                    {b.id.slice(0, 8)}
                  </td>
                  <td className="p-3 text-xs">{formatDateTime(b.created_at)}</td>
                  <td className="p-3">
                    <FileText className="inline w-3 h-3 mr-1 text-slate-500" />
                    {b.count}
                  </td>
                  <td className="p-3 text-xs">
                    <span className="inline-flex items-center gap-1 text-emerald-700">
                      <CheckCircle2 className="w-3 h-3" />
                      {b.signed_count}
                    </span>
                    {" / "}
                    <span className="inline-flex items-center gap-1 text-amber-700">
                      <Clock className="w-3 h-3" />
                      {b.count - b.signed_count}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px] mr-1"
                      onClick={() => setDetailId(b.id)}
                      data-testid={`va-detail-${b.id}`}
                    >
                      Ver
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px] text-rose-700 border-rose-200 hover:bg-rose-50"
                      onClick={() => onDelete(b.id)}
                      data-testid={`va-delete-${b.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <UploadAdvancesDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUploaded={load}
      />
      <BatchDetailDialog
        noticeId={detailId}
        open={!!detailId}
        onOpenChange={(o) => !o && setDetailId(null)}
        onReload={load}
      />
    </div>
  );
}
